import { useState } from "react";
import { createPortal } from "react-dom";
import { Monitor, Loader, WifiOff } from "lucide-react";
import FloatingWindow from "./FloatingWindow";
import API from "../api";

// =============================================================
//  RemoteButton — MeshCentral embedded PiP edition
//  Props:
//    deviceId           — UUID of the device in our DB (required)
//    deviceName         — display name shown in tooltip / window title
//    contactName        — client/user name shown in tooltip
//    meshcentralNodeId  — if truthy, device is linked and button is active
//    size               — "sm" | "md" (default "md")
//    variant            — "button" | "icon" (default "button")
// =============================================================
export default function RemoteButton({
  deviceId,
  deviceName,
  contactName,
  meshcentralNodeId,
  rustdeskId,                 // device.rustdesk_id — enables RustDesk (Main) mode
  mode      = "mesh",         // "rustdesk" (Main) | "mesh" (Backup)
  label,                      // overrides the displayed label
  size      = "md",
  variant   = "button",
  fullWidth = false,
}) {
  const [launching,   setLaunching]   = useState(false);
  const [tooltip,     setTooltip]     = useState(false);
  const [error,       setError]       = useState("");
  const [sessionUrl,  setSessionUrl]  = useState(null);
  const [windowOpen,  setWindowOpen]  = useState(false);
  const [rdSession,   setRdSession]   = useState(null);   // RustDesk connect info

  const isRustDesk = mode === "rustdesk";
  const isLinked   = isRustDesk ? !!rustdeskId : !!meshcentralNodeId;
  const isDisabled = !isLinked || launching;

  const launchRustDesk = async () => {
    if (isDisabled) return;
    setLaunching(true); setError("");
    try {
      const res = await API.get(`/devices/${deviceId}/rustdesk-session`);
      setRdSession(res.data);   // opens the MIDTC connect pop-up; user is auto-notified
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start RustDesk session.");
    } finally {
      setLaunching(false);
    }
  };

  const launch = async () => {
    if (isRustDesk) return launchRustDesk();
    if (isDisabled) return;
    // If a window is already open, just bring it into view by re-showing it
    if (windowOpen && sessionUrl) return;
    setLaunching(true);
    setError("");
    try {
      const res = await API.get(`/remote-session/${encodeURIComponent(meshcentralNodeId)}`);
      // RMM heartbeat "online" is separate from the MeshCentral agent being
      // connected. If the agent isn't connected, the viewer can't establish a
      // session — surface that instead of opening a dead window.
      if (res.data.agentOnline === false) {
        setError("The remote agent on this computer isn't currently connected to MeshCentral, so a session can't be established. Confirm the computer is powered on and online, then retry.");
        return;
      }
      setSessionUrl(res.data.url);
      setWindowOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to launch remote session.");
    } finally {
      setLaunching(false);
    }
  };

  const windowTitle = [deviceName, contactName].filter(Boolean).join(" — ");

  // ── Sizes ────────────────────────────────────────────────────
  const sizes = {
    sm: { padding: "5px 10px",  fontSize: 11, iconSize: 13, gap: 5 },
    md: { padding: "7px 14px",  fontSize: 13, iconSize: 15, gap: 7 },
  };
  const s = sizes[size] || sizes.md;

  // ── Colors — theme tokens, so the button reads in every brand theme and in light
  //    mode (the old Cyber literals put light text on a light background there).
  const bgColor     = isLinked ? "var(--t-accent-softer)" : "var(--t-input)";
  const textColor   = isLinked ? "var(--t-text-body)" : "var(--t-text-faint)";
  const borderColor = isLinked ? "var(--t-accent-soft)" : "var(--t-border-softer)";

  const svc = isRustDesk ? "RustDesk" : "MeshCentral";
  const defaultLabel = isRustDesk ? "Remote Access (Main)" : "Remote Access (Backup)";
  const tooltipText = launching
    ? `Connecting via ${svc}...`
    : windowOpen
      ? `Session active — ${deviceName || "device"}`
      : isLinked
        ? `Remote into ${deviceName || "device"}${contactName ? ` (${contactName})` : ""} via ${svc}`
        : isRustDesk
          ? `RustDesk not deployed yet on ${deviceName || "this device"}. It sets up automatically once the agent checks in.`
          : `Not linked to MeshCentral${deviceName ? ` — ${deviceName}` : ""}. Run Sync in Admin Settings.`;

  const buttonIcon = launching
    ? <Loader   size={s.iconSize} style={{ animation: "spin 1s linear infinite" }} />
    : isLinked
      ? <Monitor  size={s.iconSize} />
      : <WifiOff  size={s.iconSize} />;

  const buttonLabel = launching
    ? "Connecting..."
    : windowOpen
      ? "Session Active"
      : isLinked
        ? (label || defaultLabel)
        : (label || defaultLabel);

  // ── Icon-only variant ─────────────────────────────────────────
  if (variant === "icon") {
    return (
      <>
        <div
          style={{ position: "relative", display: "inline-flex" }}
          onMouseEnter={() => setTooltip(true)}
          onMouseLeave={() => setTooltip(false)}
        >
          <button
            onClick={launch}
            disabled={isDisabled}
            style={{
              background:     bgColor,
              border:         `1px solid ${borderColor}`,
              borderRadius:   6,
              padding:        "5px 7px",
              cursor:         isLinked ? "pointer" : "not-allowed",
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              transition:     "all 0.15s",
              color:          textColor,
            }}
            onMouseEnter={e => { if (isLinked) { e.currentTarget.style.background="var(--t-accent-soft)"; e.currentTarget.style.borderColor="var(--t-accent)"; }}}
            onMouseLeave={e => { e.currentTarget.style.background=bgColor; e.currentTarget.style.borderColor=borderColor; }}
          >
            {buttonIcon}
          </button>
          {tooltip && <Tooltip text={tooltipText} />}
          {error   && <ErrorBubble msg={error} onClose={() => setError("")} />}
        </div>
        {windowOpen && sessionUrl && (
          <FloatingWindow
            src={sessionUrl}
            title={windowTitle || "Remote Desktop"}
            onClose={() => { setWindowOpen(false); setSessionUrl(null); }}
          />
        )}
        {rdSession && <RustDeskConnectModal session={rdSession} onClose={() => setRdSession(null)} />}
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </>
    );
  }

  // ── Full button variant ───────────────────────────────────────
  return (
    <>
      <div style={{ position: "relative", display: fullWidth ? "flex" : "inline-flex", flexDirection: "column", gap: 4, width: fullWidth ? "100%" : undefined }}>
        <div
          style={{ position: "relative", display: fullWidth ? "flex" : "inline-flex", width: fullWidth ? "100%" : undefined }}
          onMouseEnter={() => setTooltip(true)}
          onMouseLeave={() => setTooltip(false)}
        >
          <button
            onClick={launch}
            disabled={isDisabled}
            style={{
              display:        "flex",
              alignItems:     "center",
              justifyContent: fullWidth ? "center" : undefined,
              gap:            s.gap,
              padding:        fullWidth ? "11px 14px" : s.padding,
              background:     windowOpen ? "rgba(34,197,94,0.12)" : bgColor,
              border:         `1px solid ${windowOpen ? "rgba(34,197,94,0.4)" : borderColor}`,
              borderRadius:   10,
              color:          windowOpen ? "#86efac" : textColor,
              boxShadow:      isLinked && !windowOpen ? "0 0 14px var(--t-accent-softer), inset 0 1px 0 rgba(255,255,255,0.06)" : "none",
              fontSize:       fullWidth ? 13 : s.fontSize,
              fontWeight:     isLinked ? 700 : 400,
              cursor:         isLinked ? "pointer" : "not-allowed",
              transition:     "all 0.2s ease",
              whiteSpace:     "nowrap",
              width:          fullWidth ? "100%" : undefined,
            }}
            onMouseEnter={e => { if (isLinked && !launching && !windowOpen) { e.currentTarget.style.background="var(--t-accent-soft)"; e.currentTarget.style.borderColor="var(--t-accent)"; e.currentTarget.style.color="var(--t-text-primary)"; e.currentTarget.style.boxShadow="0 0 20px var(--t-accent-soft), inset 0 1px 0 rgba(255,255,255,0.08)"; e.currentTarget.style.transform="translateY(-1px)"; }}}
            onMouseLeave={e => { if (!windowOpen) { e.currentTarget.style.background=bgColor; e.currentTarget.style.borderColor=borderColor; e.currentTarget.style.color=textColor; e.currentTarget.style.boxShadow=isLinked?"0 0 14px var(--t-accent-softer), inset 0 1px 0 rgba(255,255,255,0.06)":"none"; e.currentTarget.style.transform="translateY(0)"; }}}
          >
            {buttonIcon}
            {buttonLabel}
          </button>
          {tooltip && <Tooltip text={tooltipText} />}
        </div>
        {error && <ErrorBubble msg={error} onClose={() => setError("")} />}
      </div>
      {/* Portalled to <body>: the button can sit in a menu that hides itself
          (ticket page "Remote" menu) without hiding or ending the session window. */}
      {windowOpen && sessionUrl && createPortal(
        <FloatingWindow
          src={sessionUrl}
          title={windowTitle || "Remote Desktop"}
          onClose={() => { setWindowOpen(false); setSessionUrl(null); }}
        />, document.body
      )}
      {rdSession && createPortal(<RustDeskConnectModal session={rdSession} onClose={() => setRdSession(null)} />, document.body)}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}

// ── RustDesk connect pop-up (MIDTC-branded) ────────────────────
// RustDesk is a native client (not iframe-embeddable), so this MIDTC window
// surfaces the one-click connection + confirms the user was notified. The
// "Open in RustDesk" button uses the rustdesk:// protocol the client registers.
function RustDeskConnectModal({ session, onClose }) {
  const { rustdesk_id, rustdesk_password, server, key, device_name, tech, rustdesk_restarting } = session;
  const copy = (t) => { try { navigator.clipboard.writeText(t); } catch {} };
  const row = (label, val) => (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"9px 0", borderBottom:"1px solid var(--t-border-softer)" }}>
      <span style={{ fontSize:12, color:"var(--t-text-muted)" }}>{label}</span>
      <span style={{ display:"flex", alignItems:"center", gap:8 }}>
        <code style={{ fontSize:13, color:"var(--t-text-primary)", fontWeight:700 }}>{val}</code>
        <button onClick={() => copy(val)} style={{ fontSize:10, padding:"2px 7px", borderRadius:5, background:"var(--t-input)", color:"var(--t-text-muted)", border:"1px solid var(--t-border-mid)", cursor:"pointer" }}>copy</button>
      </span>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(2,6,16,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:420, maxWidth:"92vw", background:"var(--t-panel)", border:"1px solid var(--t-border-mid)", borderRadius:14, overflow:"hidden", boxShadow:"var(--t-shadow-panel)" }}>
        <div style={{ background:"#0f172a", padding:"16px 20px" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#f97316", textTransform:"uppercase", letterSpacing:1.5, marginBottom:4 }}>MIDTC Remote Support</div>
          <div style={{ fontSize:16, fontWeight:800, color:"#fff" }}>Connect to {device_name || "device"}</div>
        </div>
        <div style={{ padding:"16px 20px" }}>
          <div style={{ fontSize:12.5, color:"#34d399", marginBottom:12 }}>✓ The user has been notified that <strong>{tech?.name || "a MIDTC tech"}</strong> is connecting.</div>
          {rustdesk_restarting && (
            <div style={{ fontSize:12.5, color:"#fbbf24", background:"rgba(251,191,36,0.10)", border:"1px solid rgba(251,191,36,0.35)", borderRadius:8, padding:"8px 10px", marginBottom:12 }}>
              RustDesk wasn't running on this computer, so it's being restarted now. If RustDesk says "Remote desktop is offline", wait about a minute and connect again.
            </div>
          )}
          {row("RustDesk ID", rustdesk_id)}
          {row("Password", rustdesk_password)}
          {row("Server", server)}
          <a href={`rustdesk://${rustdesk_id}`} style={{ display:"block", textAlign:"center", marginTop:16, background:"var(--t-accent-grad)", color:"#fff", textDecoration:"none", fontWeight:700, fontSize:13, padding:"11px 0", borderRadius:8 }}>
            Open in RustDesk →
          </a>
          <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:10, lineHeight:1.6 }}>
            If RustDesk doesn't open automatically, launch it and enter the ID above (password auto-fills for unattended). Your client must use server <code>{server}</code>.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tooltip ────────────────────────────────────────────────────
function Tooltip({ text }) {
  return (
    <div style={{
      position:      "absolute",
      bottom:        "calc(100% + 8px)",
      left:          "50%",
      transform:     "translateX(-50%)",
      background:    "var(--t-panel)",
      border:        "1px solid var(--t-border-mid)",
      borderRadius:  8,
      padding:       "8px 12px",
      fontSize:      12,
      color:         "var(--t-text-body)",
      zIndex:        100,
      boxShadow:     "var(--t-shadow-panel)",
      pointerEvents: "none",
      maxWidth:      260,
      whiteSpace:    "normal",
      textAlign:     "center",
    }}>
      {text}
      <div style={{
        position:       "absolute",
        top:            "100%",
        left:           "50%",
        transform:      "translateX(-50%)",
        border:         "5px solid transparent",
        borderTopColor: "#1e293b",
      }} />
    </div>
  );
}

// ── Error bubble ───────────────────────────────────────────────
function ErrorBubble({ msg, onClose }) {
  return (
    <div style={{
      position:     "absolute",
      top:          "calc(100% + 6px)",
      left:         0,
      background:   "#7f1d1d",
      border:       "1px solid #991b1b",
      borderRadius: 8,
      padding:      "8px 12px",
      fontSize:     12,
      color:        "#fca5a5",
      zIndex:       100,
      whiteSpace:   "nowrap",
      display:      "flex",
      alignItems:   "center",
      gap:          8,
      boxShadow:    "0 4px 12px rgba(0,0,0,0.4)",
    }}>
      {msg}
      <button
        onClick={onClose}
        style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}
      >×</button>
    </div>
  );
}
